import { QueryClient } from "@tanstack/react-query";

/**
 * Standardized Caching Tiers & Query Keys for LMS Frontend
 *
 * Tier 1: Student Dynamic State (Minimal-Cache, Near-Real-Time)
 * Tier 2: Operational Workspaces (Admin & TL - staleTime: 0, refetchOnMount: true, refetchOnWindowFocus: true)
 * Tier 3: Static Taxonomies & Config (staleTime: 5 min)
 */

export const STUDENT_QUERY_OPTIONS = {
  // 30 s damper: refetchOnWindowFocus and refetchOnReconnect both respect staleTime, so this stops
  // every alt-tab and every network blip from re-firing the whole student query set (~152-request
  // fan-out on the training page). refetchOnMount: "always" deliberately ignores staleTime, so
  // navigating back to a page still refetches; the damper covers focus and reconnect only.
  staleTime: 30_000,
  gcTime: 0,
  refetchOnMount: "always" as const,
  refetchOnWindowFocus: true,
  refetchOnReconnect: true,
};

export const OPERATIONAL_QUERY_OPTIONS = {
  staleTime: 0,
  refetchOnMount: true,
  refetchOnWindowFocus: true,
  refetchOnReconnect: true,
};

export const STATIC_QUERY_OPTIONS = {
  staleTime: 5 * 60 * 1000,
  gcTime: 10 * 60 * 1000,
  refetchOnMount: true,
  refetchOnWindowFocus: false,
};

// ── Student Query Keys ───────────────────────────────────────────────────────
export const STUDENT_QUERY_KEYS = {
  trainingModules: ["training_modules"] as const,
  moduleProgress: ["module_progress"] as const,
  moduleUnlockStatus: (courseId?: string) => ["module-unlock-status", courseId ?? "all"] as const,
  courseCheckpoints: (courseId?: string) => ["course-checkpoints", courseId ?? "all"] as const,
  videoCheckpoints: (moduleId?: string) => ["video_checkpoints", moduleId ?? "none"] as const,
  availableExams: (userId?: string) => ["available-exams", userId ?? "self"] as const,
  activeExams: ["active-exams"] as const,
  pastExamSessions: (userId?: string) => ["past-exam-sessions", userId ?? "self"] as const,
  pastExamSessionsCheckpoints: (userId?: string) =>
    ["past-exam-sessions-for-checkpoints", userId ?? "self"] as const,
  studentDashboardCourses: (userId?: string) =>
    ["student_dashboard_courses", userId ?? "self"] as const,
  userCertificates: (userId?: string) => ["user-certificates", userId ?? "self"] as const,
  myCourseEnrollments: (userId?: string) => ["my-course-enrollments", userId ?? "self"] as const,
  availableCourses: (userId?: string) => ["available-courses", userId ?? "self"] as const,
  sessionReview: (sessionId?: string) => ["session-review", sessionId ?? "none"] as const,
  /** The notification bell's full list: every notification, read or not, dismissed or not. */
  notifications: (userId?: string) => ["notifications", userId ?? "self"] as const,
  /**
   * The homepage "New Announcements!" popup: ONLY undismissed NewMessage/NewAnnouncement rows,
   * from GET /notifications/popup.
   *
   * This must never share a key with `notifications` above. React Query caches by key, not by
   * queryFn, so two queries on one key are last-writer-wins — and the bell (mounted in the app
   * shell on every page, refetched on every SignalR event) always won. The popup then rendered the
   * bell's unfiltered list, showing already-dismissed system notifications forever.
   *
   * Nested UNDER the bell's key on purpose: the bell's existing
   * invalidateQueries(["notifications", userId]) calls prefix-match this key, so live SignalR
   * delivery keeps the popup fresh without any extra invalidation wiring.
   */
  popupNotifications: (userId?: string) => ["notifications", userId ?? "self", "popup"] as const,
};

// ── Team Leader Query Keys ────────────────────────────────────────────────────
export const TL_QUERY_KEYS = {
  dashboardGroups: ["tl", "groups-with-students"] as const,
  myGroups: ["tl", "groups"] as const,
  groupDetails: (id: string) => ["tl", "group", id] as const,
  assignableTracks: ["tl", "assignable", "tracks"] as const,
  assignableCourses: ["tl", "assignable", "courses"] as const,
  unreadCount: (userId: string) => ["tl", "unread-count", userId] as const,
  studentProfile: (id: string) => ["tl", "student", id] as const,
  studentCourses: (id: string) => ["tl", "student", id, "courses"] as const,
  studentExams: (id: string) => ["tl", "student", id, "exams"] as const,
  studentAttendance: (id: string) => ["tl", "student", id, "attendance"] as const,
  examsDashboard: ["tl-exams", "dashboard"] as const,
  analyticsSummary: ["tl-exams", "analytics-summary"] as const,
  examDetail: (examId: string) => ["tl-exams", "detail", examId] as const,
  studentExamProfile: (studentId: string) => ["tl-exams", "student-profile", studentId] as const,
  auditHistory: ["tl-exams", "audit-history"] as const,
  studentTimeline: (studentId: string) => ["tl-exams", "student-timeline", studentId] as const,
  atRiskStudents: ["tl-exams", "at-risk"] as const,
  statusCounts: ["tl", "status-counts"] as const,
};

// ── Admin Query Keys ──────────────────────────────────────────────────────────
export const ADMIN_QUERY_KEYS = {
  studentsGrid: ["student-grid"] as const,
  studentStatusCounts: ["student-status-counts"] as const,
  // Per-student panels inside the student-detail drawer. Declared here (rather than inline in
  // students.tsx) so the central invalidation helper below can reach them — a mounted drawer
  // never refetches on its own, so anything that mutates a student's progress must invalidate
  // these three or the drawer keeps rendering pre-mutation data.
  studentHasStudyProgress: ["student-has-study-progress"] as const,
  studentExamSessions: ["student-exam-sessions"] as const,
  studentReachableCourses: ["student-reachable-courses"] as const,
  roles: ["admin-roles"] as const,
  teamsList: ["admin-teams-list"] as const,
  activeTeamLeaders: ["active-team-leaders"] as const,
  groupsPaged: ["groups-list-paged"] as const,
  teamTrackAssignmentsAll: ["team-track-assignments-all"] as const,
  teamTracksAll: ["team-tracks-all"] as const,
  teamCourseAssignmentsAll: ["team-course-assignments-all"] as const,
  teamCoursesAll: ["team-courses-all"] as const,
  groupStudents: (groupId: string) => ["group-students", groupId] as const,
  adminCourses: ["admin-courses"] as const,
  adminCourseEdit: (courseId: string) => ["admin-course-edit", courseId] as const,
  adminCourseModules: (courseId: string) => ["admin-course-modules", courseId] as const,
  learningTracks: ["learning_tracks"] as const,
  trackCourses: (trackId: string) => ["track_courses", trackId] as const,
  questionBanks: ["question-banks"] as const,
  bankQuestions: (bankId: string) => ["bank-questions", bankId] as const,
  examsBuilder: ["exams-builder"] as const,
  examSessions: ["exam-sessions"] as const,
  gradingQueue: ["grading-queue"] as const,
  gradingHistory: ["grading-history"] as const,
  courseCurriculum: (courseId: string) => ["admin-course-curriculum", courseId] as const,
  curriculumQuestionPool: (courseId: string) => ["admin-curriculum-question-pool", courseId] as const,

  // ── Recycle Bin ────────────────────────────────────────────────────────────
  // Everything under one "trash" root so a single prefix invalidation after a restore or a permanent
  // delete refreshes the grid, the tab counts and the dashboard widget together — they are three views
  // of the same numbers, and a stale badge next to a freshly emptied tab reads as a failed action.
  trashRoot: ["trash"] as const,
  trashEntities: ["trash", "entities"] as const,
  trashDashboard: ["trash", "dashboard"] as const,
  trashList: (slug: string, params?: unknown) => ["trash", "list", slug, params ?? {}] as const,
  trashDeleters: (slug: string) => ["trash", "deleters", slug] as const,
  trashDependencies: (slug: string, id: string) => ["trash", "dependencies", slug, id] as const,
};

// ── Assignment Query Keys ─────────────────────────────────────────────────────
/**
 * Homework-with-a-correction-loop (`api/assignments/*`). One key factory for BOTH audiences,
 * because staff and student reads of the same assignment are different endpoints returning
 * different shapes — they must never collide on a key.
 *
 * The staff/student split is encoded in the key itself ("staff" vs "my" segments) rather than left
 * to the caller: two queries sharing a key are last-writer-wins regardless of their queryFn, and
 * the staff list is mounted on a page a hybrid SystemAdmin+Student can also reach as a learner.
 * Everything is nested under the single "assignments" root so one prefix invalidation after a
 * mutation refreshes every view of it.
 */
export const ASSIGNMENT_QUERY_KEYS = {
  root: ["assignments"] as const,

  // Staff
  staffList: (params?: unknown) => ["assignments", "staff", "list", params ?? {}] as const,
  staffListRoot: ["assignments", "staff", "list"] as const,
  staffDetail: (assignmentId: string) => ["assignments", "staff", "detail", assignmentId] as const,
  staffStats: (assignmentId: string) => ["assignments", "staff", "stats", assignmentId] as const,
  staffSubmissions: (assignmentId: string) =>
    ["assignments", "staff", "submissions", assignmentId] as const,
  staffStudent: (studentId: string) => ["assignments", "staff", "student", studentId] as const,
  authorableCourses: ["assignments", "staff", "authorable-courses"] as const,
  audience: (assignmentId: string) => ["assignments", "staff", "audience", assignmentId] as const,
  notDelivered: (assignmentId: string) =>
    ["assignments", "staff", "not-delivered", assignmentId] as const,
  shareTargets: (assignmentId: string) =>
    ["assignments", "staff", "share-targets", assignmentId] as const,
  reviewQueue: (params?: unknown) =>
    ["assignments", "staff", "review-queue", params ?? {}] as const,
  reviewQueueRoot: ["assignments", "staff", "review-queue"] as const,

  // A single submission's full detail (attempts + thread). Shared shape, different endpoints per
  // audience, so the audience is part of the key.
  submissionDetail: (submissionId: string) =>
    ["assignments", "staff", "submission", submissionId] as const,
  comments: (submissionId: string) => ["assignments", "comments", submissionId] as const,

  // Student
  myList: (params?: unknown) => ["assignments", "my", "list", params ?? {}] as const,
  myListRoot: ["assignments", "my", "list"] as const,
  myDetail: (assignmentId: string) => ["assignments", "my", "detail", assignmentId] as const,
};

// ── Support Ticket Query Keys ────────────────────────────────────────────────
/**
 * Support ticketing (`api/tickets/*` and `api/admin/ticket-topics/*`).
 *
 * Nested under a single "tickets" root so one prefix invalidation refreshes every view.
 * The audience split on list keys ("staff" vs "my") prevents cache collisions when a user
 * holds both administrative and student roles.
 * Detail keys are unified because both roles call the same endpoint with role-specific projections.
 */
export const TICKET_QUERY_KEYS = {
  root: ["tickets"] as const,
  studentTopics: ["tickets", "student", "topics"] as const,
  myList: (params?: unknown) => ["tickets", "my", "list", params ?? {}] as const,
  myListRoot: ["tickets", "my", "list"] as const,
  staffList: (params?: unknown) => ["tickets", "staff", "list", params ?? {}] as const,
  staffListRoot: ["tickets", "staff", "list"] as const,
  detail: (ticketId: string) => ["tickets", "detail", ticketId] as const,
  assignableStaff: (ticketId: string) => ["tickets", "assignable-staff", ticketId] as const,
  topics: (includeInactive: boolean) => ["tickets", "admin", "topics", includeInactive] as const,
  topicsRoot: ["tickets", "admin", "topics"] as const,
};

// ── Central Invalidation Helpers ──────────────────────────────────────────────

export async function invalidateAllStudentState(queryClient: QueryClient, userId?: string) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: STUDENT_QUERY_KEYS.trainingModules }),
    queryClient.invalidateQueries({ queryKey: STUDENT_QUERY_KEYS.moduleProgress }),
    queryClient.invalidateQueries({ queryKey: ["module-unlock-status"] }),
    queryClient.invalidateQueries({ queryKey: ["course-checkpoints"] }),
    queryClient.invalidateQueries({ queryKey: ["video_checkpoints"] }),
    queryClient.invalidateQueries({ queryKey: ["available-exams"] }),
    queryClient.invalidateQueries({ queryKey: ["active-exams"] }),
    queryClient.invalidateQueries({ queryKey: ["past-exam-sessions"] }),
    queryClient.invalidateQueries({ queryKey: ["past-exam-sessions-for-checkpoints"] }),
    queryClient.invalidateQueries({ queryKey: ["student_dashboard_courses"] }),
    queryClient.invalidateQueries({ queryKey: ["student-dashboard"] }),
    queryClient.invalidateQueries({ queryKey: ["user-certificates"] }),
    queryClient.invalidateQueries({ queryKey: ["my-course-enrollments"] }),
    queryClient.invalidateQueries({ queryKey: ["available-courses"] }),
  ]);
}

export async function invalidateAllTLState(queryClient: QueryClient, studentId?: string) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ["tl"] }),
    queryClient.invalidateQueries({ queryKey: ["tl-exams"] }),
    // AssignableContentPicker caches under ["assignable", kind, groupId, ...] — outside the "tl"
    // prefix. Without this, linking content leaves the picker's already-linked flags stale, so the
    // next click hits the server's duplicate-link refusal instead of showing the row disabled.
    queryClient.invalidateQueries({ queryKey: ["assignable"] }),
    queryClient.invalidateQueries({ queryKey: ["my-team-members"] }),
    queryClient.invalidateQueries({ queryKey: ["team-course-enrollments"] }),
    queryClient.invalidateQueries({ queryKey: ["team-module-progress"] }),
    studentId
      ? queryClient.invalidateQueries({ queryKey: TL_QUERY_KEYS.studentExamProfile(studentId) })
      : Promise.resolve(),
    studentId
      ? queryClient.invalidateQueries({ queryKey: TL_QUERY_KEYS.studentTimeline(studentId) })
      : Promise.resolve(),
    studentId
      ? queryClient.invalidateQueries({ queryKey: TL_QUERY_KEYS.studentProfile(studentId) })
      : Promise.resolve(),
  ]);
}

export async function invalidateAllAdminState(queryClient: QueryClient) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ADMIN_QUERY_KEYS.groupsPaged }),
    queryClient.invalidateQueries({ queryKey: ADMIN_QUERY_KEYS.teamTrackAssignmentsAll }),
    queryClient.invalidateQueries({ queryKey: ADMIN_QUERY_KEYS.teamTracksAll }),
    queryClient.invalidateQueries({ queryKey: ADMIN_QUERY_KEYS.teamCourseAssignmentsAll }),
    queryClient.invalidateQueries({ queryKey: ADMIN_QUERY_KEYS.teamCoursesAll }),
    queryClient.invalidateQueries({ queryKey: ADMIN_QUERY_KEYS.adminCourses }),
    queryClient.invalidateQueries({ queryKey: ADMIN_QUERY_KEYS.studentsGrid }),
    queryClient.invalidateQueries({ queryKey: ADMIN_QUERY_KEYS.studentStatusCounts }),
    queryClient.invalidateQueries({ queryKey: ADMIN_QUERY_KEYS.activeTeamLeaders }),
    queryClient.invalidateQueries({ queryKey: ["group-students"] }),
    queryClient.invalidateQueries({ queryKey: ["admin-course-modules"] }),
    queryClient.invalidateQueries({ queryKey: ["learning_tracks"] }),
    queryClient.invalidateQueries({ queryKey: ["question-banks"] }),
    queryClient.invalidateQueries({ queryKey: ["exams-builder"] }),
    queryClient.invalidateQueries({ queryKey: ["grading-queue"] }),
    // Same picker, admin group-detail page.
    queryClient.invalidateQueries({ queryKey: ["assignable"] }),
    // The student-detail drawer's own panels, keyed per student. The drawer stays MOUNTED across
    // a reset / force-complete, so nothing refetches them unless they are invalidated here —
    // which is why a reset appeared to "only half work": the grid row behind the drawer
    // (studentsGrid, above) refreshed while the drawer went on showing the pre-reset module
    // progress, exam status and per-course completion. Prefix keys, so every student's entry
    // matches ["<key>", studentId].
    queryClient.invalidateQueries({ queryKey: ADMIN_QUERY_KEYS.studentHasStudyProgress }),
    queryClient.invalidateQueries({ queryKey: ADMIN_QUERY_KEYS.studentExamSessions }),
    queryClient.invalidateQueries({ queryKey: ADMIN_QUERY_KEYS.studentReachableCourses }),

    // A restore from the Recycle Bin makes a record reappear everywhere at once — a restored course
    // shows up in course lists, enrolment pickers and dashboards — so a restore calls THIS helper, not
    // just its own key. Included here in the other direction too: deleting anything adds a row to the
    // bin, so the tab counts and the widget go stale on every ordinary delete in the admin panel.
    queryClient.invalidateQueries({ queryKey: ADMIN_QUERY_KEYS.trashRoot }),
  ]);
}
